// @ts-check
import { useQuery } from "@tanstack/react-query";
import { useSessionJWT } from "utils";
import { getTOSStatus } from "api/accounts";
import TiliaAPIError from "api/TiliaAPIError";
import { tosQueryKey } from "./index.js";

const isClientError = (error) =>
  error instanceof TiliaAPIError && error.status >= 400 && error.status < 500;

/**
 * useTOSStatus
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - pass `false` for surfaces that do not gate on ToS, so
 * no network call is made at all.
 * @returns the `GET /v1/user-info/tos/tilia` query
 * @description Read `isSuccess` — not `!isLoading` — to decide whether the answer is known. A query
 * disabled for want of a `sessionJWT` reports `isLoading: false` with `data: undefined`, so an
 * `isLoading`-based check would treat "never fetched" as "not signed".
 */
export default function useTOSStatus({ enabled = true } = {}) {
  const { sessionJWT, sessionData } = useSessionJWT();

  return useQuery({
    queryKey: tosQueryKey(sessionData?.account_id),
    queryFn: () => getTOSStatus(sessionJWT),
    // A signed ToS never reverts within a session; without this a remount refetches.
    staleTime: Infinity,
    // Overrides the global `retry: false` — a failure here blocks checkout. But don't retry a
    // client error: a 401 is a dead session that will never succeed, and three attempts of default
    // exponential backoff would put ~3s between the click and the "Invalid link" copy that exists
    // for exactly that case. Explicit retryDelay so tests don't need fake timers.
    retry: (count, error) => count < 2 && !isClientError(error),
    retryDelay: 300,
    // An errored query refetches whenever a new observer mounts, and a gated surface mounts several
    // (the host, its consent block, each gated leaf). Without this, one dead 401 becomes a GET per
    // component - and the retry predicate above cannot help, since each is a fresh attempt rather
    // than a retry. `ensureNotBlocked` still refetches explicitly when a click needs an answer.
    retryOnMount: false,
    enabled: enabled && Boolean(sessionJWT),
  });
}
